/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, Lock, ArrowRight, ArrowLeft, User, Phone, Home, MapPin, FileText, Eye, EyeOff, ImagePlus, Trash2 } from 'lucide-react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Select from 'react-select';
import domacinstvoService from '../api/domacinstvoService';
import authService from '../api/authService';
import { useMunicipalities } from './MunicipalitiesContext';
import { useAuth } from './AuthContext';
import { useToast } from './Toast';
import Modal from './Modal';
import { filterByPrefix, selectStyles, selectClassNames } from '../utils/selectStyles';
import { trackEvent } from '../utils/analytics';

const MAX_HOUSEHOLD_IMAGES = 7;
const MAX_IMAGE_BYTES = 9 * 1024 * 1024;
const ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AuthModal({ isOpen, onClose, initialStep = 'login' }) {
  const toast = useToast();
  const { login } = useAuth();
  const { names: gradovi } = useMunicipalities();
  const gradoviOptions = useMemo(() => gradovi.map((g) => ({ value: g, label: g })), [gradovi]);
  const [step, setStep] = useState(initialStep);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+381');
  const [imePrezime, setImePrezime] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nazivGazdinstva, setNazivGazdinstva] = useState('');
  const [lokacija, setLokacija] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 9) return digits.slice(0, 9);

    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}`;
  };

  const resetForm = useCallback(() => {
    setStep(initialStep);
    setIsEmailSent(false);
    setPhoneNumber('');
    setPhonePrefix('+381');
    setImePrezime('');
    setEmail('');
    setPassword('');
    setNazivGazdinstva('');
    setLokacija('');
    setDescription('');
    setImages([]);
    setImagePreviews((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setError('');
  }, [initialStep]);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_HOUSEHOLD_IMAGES - images.length;
    const accepted = [];
    const rejected = [];

    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
        rejected.push(`${file.name}: nepodržan format`);
        continue;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        rejected.push(`${file.name}: veća od 9 MB`);
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length > 0) {
      setImages((prev) => [...prev, ...accepted]);
      setImagePreviews((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
    }
    if (rejected.length > 0) {
      setError(rejected.join(', '));
    } else if (accepted.length > 0) {
      setError('');
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await login(email, password);
      trackEvent('login', { method: 'email' });
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Pogrešan email ili lozinka.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistracija = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = {
        domacinstvoData: {
          name: nazivGazdinstva.trim(),
          phone: `${phonePrefix}${phoneNumber.replace(/\s/g, '')}`,
          municipality: lokacija,
          description
        },
        userData: {
          name: imePrezime.trim(),
          email: email.trim(),
          password
        }
      };
      await domacinstvoService.create(data, images);
      trackEvent('sign_up', { method: 'email' });

      // Automatska prijava — korisnik ne mora ponovo da unosi kredencijale.
      try {
        await login(email.trim(), password);
        trackEvent('login', { method: 'email' });
        toast.success('Registracija uspešna. Dobro došli!');
        resetForm();
        onClose();
      } catch {
        const registeredEmail = email.trim();
        resetForm();
        setStep('login');
        setEmail(registeredEmail);
        toast.success('Registracija uspešna. Sada se prijavite.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Greška pri registraciji domaćinstva.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Email nije validan.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await authService.forgotPassword(trimmedEmail);
      setIsEmailSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Greška pri slanju email-a. Pokušajte ponovo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (step === 'register') {
      const trimmedEmail = email.trim();
      const trimmedName = imePrezime.trim();
      if (!trimmedName) { setError('Ime i prezime su obavezni.'); return; }
      if (!EMAIL_RE.test(trimmedEmail)) { setError('Email nije validan.'); return; }
      if (password.length < 6) { setError('Lozinka mora imati najmanje 6 karaktera.'); return; }
      setError('');
      setStep('household');
    } else if (step === 'forgot') {
      handleForgotPassword();
    } else if (step === 'household') {
      if (!nazivGazdinstva.trim()) { setError('Naziv gazdinstva je obavezan.'); return; }
      const phoneDigits = phoneNumber.replace(/\D/g, '');
      if (phoneDigits.length < 8) { setError('Unesite ispravan broj telefona (najmanje 8 cifara).'); return; }
      if (!lokacija) { setError('Izaberite opštinu.'); return; }
      setError('');
      handleRegistracija();
    } else if (step === 'login') {
      handleLogin();
    } else {
      onClose();
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setIsEmailSent(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      label="Prijava i registracija"
      panelClassName="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
    >
      <button
        onClick={onClose}
        aria-label="Zatvori"
        className="absolute top-6 right-6 z-10 p-2 text-sage-400 hover:text-sage-600 hover:bg-sage-50 hover:scale-110 rounded-full transition-all"
      >
        <X size={20} />
      </button>

      <div className="p-8 sm:p-10 max-h-[88dvh] overflow-y-auto custom-scrollbar">
              <div className="mb-8">
                <h2 className="text-3xl font-serif font-bold text-earth-900 mb-2">
                  {step === 'login' ? 'Dobro došli nazad' :
                   step === 'register' ? 'Postanite deo pijace' :
                   step === 'forgot' ? 'Resetujte lozinku' : 'Registracija domaćinstva'}
                </h2>
                <p className="text-sage-600">
                  {step === 'login' ? 'Prijavite se da biste upravljali vašim oglasima.' :
                   step === 'register' ? 'Kreirajte nalog i počnite sa prodajom.' :
                   step === 'forgot' ? 'Unesite vaš email kako bismo vam poslali link za resetovanje.' :
                   'Unesite osnovne podatke o vašem poljoprivrednom gazdinstvu.'}
                </p>
              </div>

              {isEmailSent && step === 'forgot' ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-sage-50 p-6 rounded-3xl border border-sage-100 text-center"
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-sage-600 mx-auto mb-4 shadow-sm">
                    <Mail size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-earth-900 mb-2">Email je poslat!</h3>
                  <p className="text-sage-600 text-sm mb-6">
                    Proverite vaše sanduče. Poslali smo vam instrukcije za resetovanje lozinke na vašu adresu.
                  </p>
                  <button
                    onClick={handleBackToLogin}
                    className="text-sage-700 font-bold hover:text-sage-900 hover:underline hover:scale-105 transition-all"
                  >
                    Vrati se na prijavu
                  </button>
                </motion.div>
              ) : (
                <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleNextStep(); }}>
                <AnimatePresence mode="popLayout">
                  {step === 'register' && (
                    <motion.div
                      key="register-fields"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Ime i prezime</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                          <input
                            type="text"
                            value={imePrezime}
                            onChange={(e) => setImePrezime(e.target.value)}
                            placeholder="Marko Marković"
                            className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 'household' && (
                    <motion.div
                      key="household-fields"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Naziv gazdinstva</label>
                        <div className="relative">
                          <Home className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                          <input
                            type="text"
                            value={nazivGazdinstva}
                            onChange={(e) => setNazivGazdinstva(e.target.value)}
                            placeholder="Gazdinstvo Petrović"
                            className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Broj telefona</label>
                        <div className="flex gap-2">
                          <div className="relative">
                            <select
                              value={phonePrefix}
                              onChange={(e) => setPhonePrefix(e.target.value)}
                              className="h-full bg-sage-50 border border-sage-100 rounded-2xl pl-3 pr-8 py-3.5 text-sm font-bold text-earth-900 focus:ring-2 focus:ring-sage-200 outline-none appearance-none cursor-pointer"
                            >
                              <option value="+381">🇷🇸 +381</option>
                              <option value="+385">🇭🇷 +385</option>
                              <option value="+382">🇲🇪 +382</option>
                              <option value="+387">🇧🇦 +387</option>
                            </select>
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-sage-400">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                            </div>
                          </div>
                          <div className="relative flex-1">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                            <input
                              type="tel"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                              placeholder="60 123 4567"
                              className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                            />
                          </div>
                        </div>
                        <p className="mt-1.5 text-xs text-sage-600 ml-1 italic">Kupci će vas kontaktirati na ovaj broj.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Lokacija / Opština</label>
                        <div className="relative">
                          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                          <Select
                            options={gradoviOptions}
                            value={gradoviOptions.find((o) => o.value === lokacija) || null}
                            onChange={(opt) => setLokacija(opt?.value || '')}
                            placeholder="Izaberite opštinu (kucajte za pretragu)"
                            isSearchable
                            isClearable
                            filterOption={filterByPrefix}
                            noOptionsMessage={() => 'Nema rezultata'}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            styles={selectStyles}
                            unstyled
                            classNames={selectClassNames}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Opis</label>
                        <div className="relative">
                          <FileText className="absolute left-4 top-4 text-sage-400" size={18} />
                          <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Kratak opis vašeg gazdinstva..."
                            rows={3}
                            className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all resize-y min-h-[80px]"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">
                          Fotografije gazdinstva <span className="text-sage-400 font-normal">(opciono, maks. {MAX_HOUSEHOLD_IMAGES})</span>
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {imagePreviews.map((src, idx) => (
                            <div key={idx} className="relative aspect-square rounded-xl overflow-hidden group border border-sage-100 shadow-sm">
                              <img src={src} alt={`Slika ${idx + 1}`} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removeImage(idx)}
                                aria-label="Ukloni sliku"
                                className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                          {images.length < MAX_HOUSEHOLD_IMAGES && (
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="aspect-square bg-sage-50 border-2 border-dashed border-sage-200 rounded-xl flex flex-col items-center justify-center gap-1 hover:bg-sage-100 hover:border-sage-300 transition-all text-sage-600"
                            >
                              <ImagePlus size={20} />
                              <span className="text-[11px] font-bold">Dodaj sliku</span>
                            </button>
                          )}
                        </div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          multiple
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                        />
                        <p className="mt-1.5 text-xs text-sage-500 ml-1 italic">JPG, PNG ili WEBP. Do 9 MB po slici.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {(step === 'login' || step === 'register' || step === 'forgot') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Email adresa</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="primer@email.com"
                          className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                        />
                      </div>
                    </div>

                    {step !== 'forgot' && (
                      <div>
                        <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Lozinka</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-12 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            tabIndex={-1}
                            aria-label={showPassword ? 'Sakrij lozinku' : 'Prikaži lozinku'}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sage-400 hover:text-sage-700 transition-colors"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3">
                    {error}
                  </div>
                )}

                {step === 'login' && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setStep('forgot')}
                      className="text-sm font-medium text-sage-700 hover:text-sage-900 hover:underline transition-all"
                    >
                      Zaboravili ste lozinku?
                    </button>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  {step === 'household' && (
                    <motion.button
                      whileHover={!isLoading ? { scale: 1.03 } : {}}
                      whileTap={!isLoading ? { scale: 0.98 } : {}}
                      type="button"
                      disabled={isLoading}
                      onClick={() => { setStep('register'); setError(''); }}
                      className="px-5 py-4 bg-white border-2 border-sage-200 text-sage-800 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-sage-50 hover:border-sage-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft size={20} />
                      Nazad
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={!isLoading ? { scale: 1.03 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-sage-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-sage-600/20 hover:bg-sage-700 hover:shadow-xl hover:shadow-sage-700/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Slanje...' :
                     step === 'login' ? 'Prijavi se' :
                     step === 'register' ? 'Nastavi na podatke o domaćinstvu' :
                     step === 'forgot' ? 'Pošalji link za resetovanje' : 'Završi registraciju'}
                    {!isLoading && <ArrowRight size={20} />}
                  </motion.button>
                </div>
              </form>
            )}

              {step !== 'forgot' && (
                <p className="mt-8 text-center text-sm text-sage-600">
                  {step === 'login' ? 'Nemate nalog?' : 'Već imate nalog?'}
                  <button
                    onClick={() => setStep(step === 'login' ? 'register' : 'login')}
                    className="ml-1 font-bold text-sage-700 hover:text-sage-900 hover:scale-105 inline-block underline underline-offset-4 transition-all"
                  >
                    {step === 'login' ? 'Registrujte se besplatno' : 'Prijavite se ovde'}
                  </button>
                </p>
              )}

              {step === 'forgot' && !isEmailSent && (
                <p className="mt-8 text-center text-sm text-sage-600">
                  Vratite se na
                  <button
                    onClick={handleBackToLogin}
                    className="ml-1 font-bold text-sage-700 hover:text-sage-900 hover:scale-105 inline-block underline underline-offset-4 transition-all"
                  >
                    prijavu
                  </button>
                </p>
              )}
      </div>
    </Modal>
  );
}
